(() => {
  if (!('customElements' in window) || customElements.get('g-viewport-video')) return;

  const START_RATIO = 0.2;
  const YOUTUBE_PLAYER_OPEN_ATTRIBUTE = 'data-g-youtube-player-open';
  const SYNC_EVENT = 'g:viewport-video:sync';
  const elements = new Set();
  const states = new WeakMap();
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let fallbackFrame = 0;

  const pageIsHidden = () => document.hidden || document.visibilityState === 'hidden';

  const getVideo = (element) => element.querySelector('video');

  const getState = (element) => {
    let state = states.get(element);
    if (!state) {
      state = {
        viewportEligible: false,
        playPending: false,
        requestId: 0,
      };
      states.set(element, state);
    }
    return state;
  };

  const prepareVideo = (video) => {
    if (!video) return;
    // 自动播放需要 DOM 属性与 JS property 同时保持静音，兼容 Safari 和移动端浏览器策略。
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');
  };

  const shouldPlay = (element, state) =>
    element.isConnected &&
    element.getAttribute('data-active') !== 'false' &&
    document.documentElement.getAttribute(YOUTUBE_PLAYER_OPEN_ATTRIBUTE) !== 'true' &&
    state.viewportEligible &&
    !pageIsHidden() &&
    !reduceMotionQuery.matches;

  const pause = (element, state) => {
    const video = getVideo(element);
    state.requestId += 1;
    state.playPending = false;
    if (video && !video.paused) video.pause();
  };

  const sync = (element) => {
    if (!elements.has(element)) return;
    const state = getState(element);
    const video = getVideo(element);
    if (!video) return;
    prepareVideo(video);

    // 轮播 active、弹窗暂停与视口状态共同决定播放，避免不可见媒体在后台持续解码。
    if (!shouldPlay(element, state)) {
      pause(element, state);
      return;
    }

    if (!video.paused || state.playPending) return;

    const requestId = state.requestId + 1;
    state.requestId = requestId;
    state.playPending = true;

    let playPromise;
    try {
      playPromise = video.play();
    } catch (error) {
      state.playPending = false;
      return;
    }

    if (!playPromise || typeof playPromise.then !== 'function') {
      state.playPending = false;
      if (!shouldPlay(element, state)) video.pause();
      return;
    }

    playPromise
      .then(() => {
        if (state.requestId !== requestId) {
          if (!shouldPlay(element, state)) video.pause();
          return;
        }
        state.playPending = false;
        if (!shouldPlay(element, state)) video.pause();
      })
      .catch(() => {
        // 节电或流量策略可能拒绝 play()；保留 poster 即可，不把它视为运行时错误。
        if (state.requestId === requestId) state.playPending = false;
      });
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const state = getState(entry.target);
              if (!entry.isIntersecting) {
                state.viewportEligible = false;
              } else if (!state.viewportEligible && entry.intersectionRatio >= START_RATIO) {
                state.viewportEligible = true;
              }
              sync(entry.target);
            });
          },
          { threshold: [0, START_RATIO] }
        )
      : null;

  const measureFallback = () => {
    fallbackFrame = 0;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    elements.forEach((element) => {
      const state = getState(element);
      const rect = element.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      const area = Math.max(0, rect.width * rect.height);
      const ratio = area > 0 ? (visibleWidth * visibleHeight) / area : 0;

      if (ratio === 0) {
        state.viewportEligible = false;
      } else if (!state.viewportEligible && ratio >= START_RATIO) {
        state.viewportEligible = true;
      }
      sync(element);
    });
  };

  const scheduleFallback = () => {
    if (observer || fallbackFrame) return;
    fallbackFrame = window.requestAnimationFrame(measureFallback);
  };

  const register = (element) => {
    if (elements.has(element)) return;
    elements.add(element);
    prepareVideo(getVideo(element));
    if (observer) {
      observer.observe(element);
    } else {
      scheduleFallback();
    }
  };

  const unregister = (element) => {
    if (!elements.has(element)) return;
    const state = getState(element);
    pause(element, state);
    if (observer) observer.unobserve(element);
    elements.delete(element);
  };

  const syncAll = () => {
    elements.forEach((element) => sync(element));
  };

  document.addEventListener(SYNC_EVENT, syncAll);

  if (!observer) {
    window.addEventListener('scroll', scheduleFallback, { passive: true });
    document.addEventListener('scroll', scheduleFallback, { passive: true, capture: true });
    window.addEventListener('resize', scheduleFallback);
    window.addEventListener('orientationchange', scheduleFallback);
  }

  document.addEventListener('visibilitychange', () => {
    // 页面隐藏时立即暂停，避免后台标签页继续解码并消耗电量。
    syncAll();
  });
  window.addEventListener('pagehide', () => {
    elements.forEach((element) => pause(element, getState(element)));
  });
  window.addEventListener('pageshow', syncAll);

  if (typeof reduceMotionQuery.addEventListener === 'function') {
    reduceMotionQuery.addEventListener('change', syncAll);
  } else if (typeof reduceMotionQuery.addListener === 'function') {
    reduceMotionQuery.addListener(syncAll);
  }

  class GViewportVideo extends HTMLElement {
    static get observedAttributes() {
      return ['data-active'];
    }

    connectedCallback() {
      register(this);
    }

    disconnectedCallback() {
      unregister(this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'data-active' || oldValue === newValue) return;
      if (observer) {
        sync(this);
      } else {
        scheduleFallback();
      }
    }
  }

  customElements.define('g-viewport-video', GViewportVideo);
})();
